import { useState, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BANKER_EXTENDS } from "@/contants";
import {
  CHANGE_BOOKMAKER_EXTEND,
  SET_ODDS_DISPLAY_TOGGLES,
} from "@/lib/redux/reducers/actionTypes";
import { computeOddsMarketClick } from "@/utils/dreamnOddsDisplayToggle";
import dreamnIconClose from "@/assets/images/dreamn-icon-close.svg";
import dreamnIconCloseHover from "@/assets/images/dreamn-icon-close-hover.svg";
import dreamnIconSetting from "@/assets/images/dreamn-icon-setting.svg";

const WARNING_MS = 3200;

const DreamnSetting = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [oddsWarning, setOddsWarning] = useState(null);
  const warningTimerRef = useRef(null);

  const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer);
  const toggleState = useSelector((state) => state.toggleReducer);

  useEffect(() => {
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, []);

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `${item.shortName}`,
      })),
    [],
  );

  const togglePopup = () => setIsOpen(!isOpen);

  const handleOddsChange = (key, e) => {
    e?.stopPropagation?.();
    const prev = {
      hdc: toggleState.hdc,
      ou: toggleState.ou,
      x2: toggleState.x2,
    };
    const { next, warning } = computeOddsMarketClick(prev, key);

    if (warning) {
      setOddsWarning(warning);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      warningTimerRef.current = setTimeout(() => setOddsWarning(null), WARNING_MS);
      return;
    }

    setOddsWarning(null);
    dispatch({
      type: SET_ODDS_DISPLAY_TOGGLES,
      payload: next,
    });
  };

  const handleTypeChange = (val) => {
    dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val });
  };

  return (
    <div className="relative">
      <div
        className="flex flex-row items-center gap-2 cursor-pointer select-none"
        onClick={togglePopup}
      >
        <img src={dreamnIconSetting} alt="" width={24} height={24} className="shrink-0" />
        <span className="text-[var(--theme-primary)] font-bold text-sm">Cài đặt</span>
      </div>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-40 transition-opacity duration-300"
            onClick={togglePopup}
            aria-hidden
          />
          <div className="fixed top-[15%] left-1/2 -translate-x-1/2 w-[95%] max-w-[550px] min-h-[240px] bg-[var(--theme-foreground)] rounded-xl z-50 overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] border-2 border-solid border-[var(--theme-primary)] animate-in fade-in zoom-in duration-300">
            <div className="relative flex justify-center items-center px-6 py-4 bg-gray-50/50 border-b border-gray-100">
              <h3 className="text-[var(--theme-primary)] font-extrabold uppercase text-lg tracking-wider">
                Cài đặt
              </h3>
              <button
                type="button"
                className="group absolute right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-50 transition-all duration-200"
                onClick={togglePopup}
                aria-label="Đóng"
              >
                <img
                  src={dreamnIconClose}
                  alt=""
                  className="w-5 h-5 shrink-0 group-hover:hidden"
                  aria-hidden
                />
                <img
                  src={dreamnIconCloseHover}
                  alt=""
                  className="hidden w-5 h-5 shrink-0 group-hover:block"
                  aria-hidden
                />
              </button>
            </div>

            <div className="p-6 pt-4 flex flex-col gap-4">
              {oddsWarning && (
                <div className="text-center text-xs py-1.5 pl-1 pr-4 rounded-md kbv-fixture-popup-warning-bg-navy-slate text-[#842029]">
                  {oddsWarning}
                </div>
              )}

              <div className="flex items-start gap-1">
                <div className="w-1/5 shrink-0 text-sm text-[var(--theme-text-muted)] pt-0.5">
                  Tỷ lệ
                </div>
                <div className="w-4/5 flex min-w-0 flex-col gap-1">
                  <p className="text-[11px] leading-snug text-[var(--theme-text-muted)]">
                    Chọn 2–3 loại hiển thị trên mỗi trận (HDP, Tài xỉu, 1x2).
                  </p>
                  <div className="flex overflow-hidden rounded border-t border-b border-l border-solid border-[var(--theme-primary)] h-[23px]">
                  <div
                    className={`flex-1 flex items-center justify-center cursor-pointer text-xs border-r border-solid border-[var(--theme-primary)] ${
                      toggleState.hdc
                        ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] font-bold"
                        : "bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                    }`}
                    onClick={(e) => handleOddsChange("hdc", e)}
                  >
                    HDP
                  </div>
                  <div
                    className={`flex-1 flex items-center justify-center cursor-pointer text-xs border-r border-solid border-[var(--theme-primary)] ${
                      toggleState.ou
                        ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] font-bold"
                        : "bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                    }`}
                    onClick={(e) => handleOddsChange("ou", e)}
                  >
                    Tài xỉu
                  </div>
                  <div
                    className={`flex-1 flex items-center justify-center cursor-pointer text-xs border-r border-solid border-[var(--theme-primary)] ${
                      toggleState.x2
                        ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] font-bold"
                        : "bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                    }`}
                    onClick={(e) => handleOddsChange("x2", e)}
                  >
                    1x2
                  </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-1/5 text-sm text-[var(--theme-text-muted)]">Loại kèo</div>
                <div className="w-4/5 flex overflow-hidden rounded border-t border-b border-l border-solid border-[var(--theme-primary)] h-[23px]">
                  {bookmakerExtendOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`flex-1 flex items-center justify-center cursor-pointer text-[10px] sm:text-xs border-r border-solid border-[var(--theme-primary)] ${
                        bookmarkerExtend === opt.value
                          ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] font-bold"
                          : "bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                      }`}
                      onClick={() => handleTypeChange(opt.value)}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DreamnSetting;
